import React, { useEffect, useState } from 'react';
import { Globe, AlertCircle, X, ArrowRight, RotateCcw } from 'lucide-react';
import { MarshmallowSkewer } from '../MarshmallowSkewer';
import type { ConflictInfo } from '../../../hooks/useRoaryImport';

interface ImportUrlStepProps {
  onSubmit: (url: string) => void;
  onClose: () => void;
  onBack?: () => void;
  layoutName?: string | null;
  loading?: boolean;
  error?: string | null;
  conflict?: ConflictInfo | null;
  onCancelInFlight?: () => Promise<void> | void;
  onCancelAndRetry?: (url: string) => void;
}

const SCAN_FEE = 5;

export const ImportUrlStep: React.FC<ImportUrlStepProps> = ({
  onSubmit,
  onClose,
  onBack,
  layoutName,
  loading,
  error,
  conflict,
  onCancelInFlight,
  onCancelAndRetry,
}) => {
  const [url, setUrl] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (!conflict?.sourceUrl) return;
    setUrl(current =>
      current.trim() ? current : conflict.sourceUrl!.replace(/^https?:\/\//i, '')
    );
  }, [conflict]);

  const trimmed = url.trim();
  const looksValid = /^[a-z0-9][a-z0-9.-]*\.[a-z]{2,}/i.test(
    trimmed.replace(/^https?:\/\//i, '').replace(/^www\./i, '')
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!looksValid || !acknowledged) return;
    onSubmit(trimmed);
  };

  return (
    <div className="flex flex-col h-full bg-slate-900 text-white">
      <div className="flex items-center justify-between px-6 py-4 border-b border-slate-800">
        <div className="flex items-center gap-3">
          <Globe className="w-5 h-5 text-orange-400" />
          <h2 className="text-lg font-semibold">Import an existing website</h2>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-slate-800 transition-colors"
          aria-label="Close"
        >
          <X size={18} />
        </button>
      </div>

      <form className="flex-1 px-6 py-6 overflow-y-auto" onSubmit={handleSubmit}>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="mb-4 text-sm text-slate-400 hover:text-white transition-colors"
          >
            ← Change template
            {layoutName ? ` (${layoutName})` : ''}
          </button>
        )}

        <p className="text-slate-300 mb-6">
          Paste the URL of any public website. I'll scan the homepage, pull brand colors and images,
          then let you pick extra pages from the nav (up to 8). You'll review the plan before
          anything's built.
        </p>

        <label htmlFor="import-url" className="block text-sm font-medium text-slate-200 mb-2">
          Website URL
        </label>
        <input
          id="import-url"
          type="text"
          autoFocus
          autoComplete="off"
          spellCheck={false}
          value={url}
          onChange={e => setUrl(e.target.value)}
          placeholder="example.com"
          disabled={loading}
          className="w-full px-4 py-3 rounded-lg bg-slate-800 border border-slate-700 focus:border-orange-500 focus:ring-2 focus:ring-orange-500/30 outline-none text-white placeholder:text-slate-500"
        />
        <p className="mt-1 text-xs text-slate-500">
          Public https sites only. Localhost, private IPs, and competepages.com are not supported.
        </p>

        <div className="mt-5 flex items-center gap-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
          <MarshmallowSkewer className="w-7 h-7 flex-shrink-0" />
          <div className="text-sm text-amber-200">
            Scanning costs <strong>{SCAN_FEE} marshmallows</strong>. The AI analysis and build use
            additional marshmallows based on the site size.
          </div>
        </div>

        <label className="mt-5 flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={acknowledged}
            onChange={e => setAcknowledged(e.target.checked)}
            className="mt-1 h-4 w-4 rounded border-slate-600 bg-slate-800 text-orange-500 focus:ring-orange-500"
          />
          <span className="text-xs text-slate-400">
            I have permission to import this website. I understand Compete Pages respects
            <code className="mx-1 px-1 py-0.5 rounded bg-slate-800 text-slate-300">robots.txt</code>
            and rate-limits each scan.
          </span>
        </label>

        {error && !conflict && (
          <div className="mt-5 flex items-start gap-2 p-3 rounded-lg bg-red-500/10 border border-red-500/30">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-300">{error}</p>
          </div>
        )}

        {conflict && (
          <div className="mt-5 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-sm text-amber-200 font-medium">
                  You already have an import in progress
                </p>
                <p className="mt-1 text-xs text-amber-300/80">
                  {conflict.sourceOrigin ? (
                    <>
                      Started for{' '}
                      <span className="font-mono text-amber-100">{conflict.sourceOrigin}</span>
                      {conflict.status ? ` — status: ${conflict.status}` : ''}
                      {conflict.createdAt
                        ? ` (${new Date(conflict.createdAt).toLocaleString()})`
                        : ''}
                    </>
                  ) : (
                    <>Cancel it to start a new one.</>
                  )}
                </p>
              </div>
            </div>
            <div className="mt-3">
              <button
                type="button"
                onClick={() => {
                  const next = trimmed || conflict.sourceUrl || '';
                  if (next) {
                    onCancelAndRetry?.(next);
                    return;
                  }
                  void onCancelInFlight?.();
                }}
                disabled={loading}
                className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 disabled:opacity-50 disabled:cursor-not-allowed border border-amber-500/40 text-amber-100 text-sm font-medium transition-colors"
              >
                <RotateCcw size={14} />
                Cancel previous import and start over
              </button>
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={!looksValid || !acknowledged || loading}
          className="mt-6 w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-orange-500 hover:bg-orange-400 disabled:bg-slate-700 disabled:text-slate-500 text-white font-semibold transition-colors"
        >
          {loading ? (
            'Starting…'
          ) : (
            <>
              Start scan
              <ArrowRight size={16} />
            </>
          )}
        </button>
      </form>
    </div>
  );
};
